var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.album",
  title: "EGT album detail page",
  description: "One album's detail page — frosted hero (cover, title, released/prerelease posture, streaming or presave) + the ordered tracklist. Reads the single ALBUMS source in egt.sections.js by id; Exploit tracks additionally show character POV + a one-line feeling descriptor.",
  supportServer: true,
  fields: {
    albumId: { title: "Album id", type: "string",
      description: "The id of the album to render (matches an ALBUMS entry in egt.sections.js).",
      required: true }
  }
});
var S = require("../helpers/egt.sections.js");
/* The per-track ROW (title + Exploit "Character:" POV + feeling + the
 * fs.existsSync-gated preview player + the --solo modifier) is the SHARED
 * builder in app/helpers/egt.trackrow.js — the same rows /exploit renders in its
 * soundtrack tabs, so the two surfaces can never drift. */
var TR = require("../helpers/egt.trackrow.js");

function findAlbum(id) {
  id = (id || "").toString();
  var list = S.ALBUMS || [];
  for (var i = 0; i < list.length; i++) { if (list[i].id === id) { return list[i]; } }
  return null;
}

/* Per-surface JSON-LD: WebPage + BreadcrumbList + one MusicAlbum with its
 * numberOfTracks and an itemListElement of MusicRecording tracks. Only real,
 * on-page facts — the streaming links as `sameAs` (released only), the cover
 * image, the ASCAP credit, and (released only) datePublished = the album's real
 * releaseDate, which matches Apple's catalog (decision D001: dates are fine; only a
 * platform release date is off-limits). A prerelease declares numberOfTracks + the
 * ordered titles but no sameAs and no datePublished. */
function albumSchema(a, streamable) {
  var O = S.siteOrigin();
  var titles = (a.tracks || []).map(function (t) { return (t && typeof t === "object") ? t.title : t; });
  var albumNode = {
    "@type": "MusicAlbum",
    "@id": O + "/entertainment/" + a.id + "#album",
    url: O + "/entertainment/" + a.id,
    name: a.nm,
    byArtist: { "@type": "MusicGroup", name: "EGT Studios" },
    image: O + a.art,
    numTracks: titles.length,
    albumReleaseType: a.meta === "Single"
      ? "https://schema.org/SingleRelease"
      : "https://schema.org/AlbumRelease",
    track: titles.map(function (title, i) {
      return { "@type": "MusicRecording", position: i + 1, name: title,
        byArtist: { "@type": "MusicGroup", name: "EGT Studios" } };
    })
  };
  if (streamable) {
    albumNode.sameAs = [ a.apple, a.spotify, a.youtubeAlbum || a.youtube ].filter(Boolean);
    if (a.releaseDate) { albumNode.datePublished = a.releaseDate; }
  }
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/entertainment/" + a.id + "#webpage",
      url: O + "/entertainment/" + a.id, name: a.nm,
      isPartOf: { "@id": O + "/#website" },
      breadcrumb: { "@id": O + "/entertainment/" + a.id + "#breadcrumb" } },
    { "@type": "BreadcrumbList", "@id": O + "/entertainment/" + a.id + "#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Entertainment", item: O + "/entertainment" },
        { "@type": "ListItem", position: 3, name: a.nm }
      ] },
    albumNode
  ]);
}

exports.render = async function render(options) {
  options = options || {};
  var reserve = options.reserveUrl || "/reserve";
  var a = findAlbum(options.albumId);

  /* Unknown / missing id — a plain, on-brand not-found beat rather than an
   * error page (the slug list gates real routes, so this is defensive only). */
  if (!a) {
    return S.page(reserve, [
      S.section({
        eyebrow: "Entertainment",
        title: "We couldn't find that record.",
        lead: "The album you're looking for isn't here.",
        body: [ { n: "p", i: { n: "a", href: "/entertainment", c: "egt-cta",
          i: [ "Back to Entertainment", { n: "span", c: "egt-cta__tick", i: " ›" } ] } } ]
      })
    ]);
  }

  var released = S.albumReleased(a);
  /* Posture keys off STREAMABLE (released AND ≥1 link), never raw albumReleased:
   * a released record with no links yet (Act 3 the moment its date passes but
   * before links land) shows the presave posture, never an empty pills box. The
   * "Releases on <date>" line keys off albumReleased so a past-dated record never
   * prints a stale future date. */
  var streamable = S.albumStreamable(a);
  var tracks = a.tracks || [];
  var trackCountLine = tracks.length + (tracks.length === 1 ? " track" : " tracks");

  /* HERO posture block — streamable: icon-only streaming links; otherwise:
   * "Releases on <date>" (only while genuinely still upcoming) + a DistroKid
   * presave CTA (never streaming links, so the page never implies the record is
   * streamable yet). */
  var posture;
  if (streamable) {
    posture = [
      { n: "div", c: "egt-album-hero__stream", role: "group",
        "aria-label": "Listen to " + a.nm,
        i: S.streamLinks(a.apple, a.spotify, a.youtube) }
    ];
  } else {
    posture = [];
    if (!released) {
      posture.push({ n: "p", c: "egt-album-hero__release",
        i: [ "Releases on ", { n: "span", c: "egt__gold", i: S.albumReleaseDateLong(a) } ] });
    }
    /* FOLLOW THE ARTIST — while the record isn't streamable yet, a shared
     * prerelease link should still let people listen/follow/subscribe. These
     * point at the ARTIST pages (not the album), so they're valid before the
     * album's own DSP URLs exist; once the album is streamable, the album pills
     * above take over and this row no longer renders. */
    if (a.artist && (a.artist.apple || a.artist.spotify || a.artist.youtube)) {
      posture.push({ n: "p", c: "egt-album-hero__release", i: "Listen & follow the artist" });
      posture.push({ n: "div", c: "egt-album-hero__stream", role: "group",
        "aria-label": "Listen and follow the artist on Apple Music, Spotify and YouTube Music",
        i: S.streamLinks(a.artist.apple, a.artist.spotify, a.artist.youtube) });
    }
    if (a.presaveUrl) {
      posture.push({ n: "a", href: a.presaveUrl, target: "_blank", rel: "noopener noreferrer",
        c: "egt-cta egt-album-hero__presave",
        "aria-label": "Pre-save " + a.nm + " on DistroKid",
        i: [ "Pre-save on DistroKid", { n: "span", c: "egt-cta__tick", i: " ›" } ] });
    }
  }

  /* BOTTOM posture — a repeat of the hero's listen/pre-save affordance at the
   * END of the tracklist (so a reader who scrolled the whole list doesn't have
   * to travel back up to act). Streamable → the same icon-only streaming pills;
   * otherwise → the same DistroKid pre-save CTA (never streaming links, so the
   * page never implies a not-yet-streamable record is streamable). */
  var bottomPosture = null;
  if (streamable) {
    bottomPosture = { n: "div", c: "egt-album-list__stream", role: "group",
      "aria-label": "Listen to " + a.nm,
      i: S.streamLinks(a.apple, a.spotify, a.youtube) };
  } else if (a.presaveUrl) {
    bottomPosture = { n: "a", href: a.presaveUrl, target: "_blank", rel: "noopener noreferrer",
      c: "egt-cta egt-album-list__presave",
      "aria-label": "Pre-save " + a.nm + " on DistroKid",
      i: [ "Pre-save on DistroKid", { n: "span", c: "egt-cta__tick", i: " ›" } ] };
  }

  var heroCard = S.glass({ width: "wide", c: "egt-album-hero__card egt-reveal", content: [
    { n: "div", c: "egt-album-hero__cover",
      i: { n: "img", c: "egt-album-hero__img", src: a.art, alt: a.nm + " — album art",
        width: "600", height: "600", loading: "eager", decoding: "async" } },
    { n: "div", c: "egt-album-hero__meta",
      i: [
        { n: "p", c: "egt__eyebrow", i: streamable ? "The records" : "Coming to the records" },
        { n: "h1", c: "egt-album-hero__title", i: a.nm },
        { n: "p", c: "egt-album-hero__sub", i: a.meta },
        { n: "p", c: "egt-album-hero__count", i: trackCountLine },
        { n: "div", c: "egt-album-hero__posture", i: posture }
      ] }
  ] });

  /* BOOK — Exploit records ARE the novel's soundtrack, so funnel music traffic
   * (especially DSP/social inbound on release days) to the book's MAIN page, which
   * funnels to both the free opening and the preorder. Only on Exploit albums;
   * other records don't carry the book CTA. Null → the .filter(Boolean) below
   * drops the beat entirely. */
  var isExploit = /^exploit/i.test(a.id || "");

  /* FREE SAMPLE — the ONE capture on this page, on EVERY album detail page
   * (founder directive 2026-08-21: "make sure that all albums have a call to
   * action on the page to get your free samples"). This replaces the old
   * per-album `newsletter` opt-in, which only ever fired on one record; the flag
   * has been removed from ALBUMS so there is exactly one capture here and no
   * chance of rendering two (their DOM ids would collide — capture() derives
   * every id from the source literal).
   *
   * source:"album" is already a key of SOURCES (egt.capture.js → the ONE
   * `general` newsletter) AND of PROMISED_SOURCES (egt.free-sample.js), so the
   * free opening of the novel is minted and delivered on confirm with ZERO
   * backend change. Do NOT invent a new source literal here — an unknown one
   * collapses to "footer" and silently promises a book it will never send.
   *
   * TWO HONEST COPY VARIANTS. The Exploit acts ARE the novel's score, so they
   * may say the songs come from the story. Every other record — including Push
   * Past Pain, which is drawn from Isaiah Carter's real life and is NOT scored
   * to the book — gets the studio framing instead. Never tell a listener a
   * record is scored to a novel that it isn't. */
  var sampleCta = { n: "section", c: "egt-section egt-album-signup",
    i: { n: "div", c: "egt__shell egt-reveal",
      i: S.capture({
        source: "album",
        variant: "panel",
        eyebrow: "Free chapters",
        title: isExploit
          ? [ "The story these songs come from — ", { n: "span", c: "egt__gold", i: "read it free." } ]
          : [ "The studio also writes books — ", { n: "span", c: "egt__gold", i: "read one free." } ],
        lead: isExploit
          ? "Every track on this record is sung from inside Exploit, Book One of The Universe. Tell us where to send it and the opening chapters are yours — free."
          : "The studio behind this music also publishes a novel: Exploit, Book One of The Universe. Tell us where to send it and the opening chapters are yours — free.",
        label: "Where should we send it?",
        cta: "Send me the free chapters",
        /* Marketing-list consent: REQUIRED, never pre-checked (founder ruling
         * 2026-07-22). Every non-footer door renders the tick. The label names
         * the free opening, because that is what this door actually promises —
         * same wording as the other book doors (/exploit, /exploit/free, /ig). */
        consent: true,
        consentLabel: [ "Yes — email me the free opening of ", { n: "em", i: "Exploit" }, ", then new chapters, album drops and news from EGT. I can unsubscribe from any email." ],
        /* THE CONFIRM/SPAM DIALOG — mandatory on any door that promises the
         * book. Delivery is gated on the double-opt-in CLICK (egt.free-sample.js
         * only mints the sample on confirm), so a reader who never finds the
         * mail never gets the chapters they were promised. confirmDialog()
         * returns null unless `dialog` is literally true, and the inline one-line
         * note is far too quiet to carry "check your Spam/Junk". Every other live
         * PROMISED_SOURCES door renders it (egt.exploit.free.js, egt.exploit.js,
         * egt.ig.swole.js); this one must too. */
        dialog: true,
        dialogLead: isExploit
          ? "You're confirmed, and the free opening of Exploit, the novel these songs are scored to, is yours. Tap Read your free chapters to start."
          : "You're confirmed, and the free opening of Exploit, the studio's novel, is yours. Tap Read your free chapters to start."
      }) } };
  /* Contextual link to the free-opening page itself (SEO wave 2C-13), under
   * the capture: a reader who wants to see what the free chapters are before
   * signing up can, and /exploit/free gains an inbound link from every album. */
  sampleCta.i.i = [ sampleCta.i.i,
    { n: "p", c: "egt-album-signup__more",
      i: { n: "a", href: "/exploit/free", c: "egt-cta egt-cta--text",
        i: [ "See what's in the free opening of Exploit", { n: "span", c: "egt-cta__tick", i: " ›" } ] } } ];

  var bookCta = isExploit ? S.section({
    eyebrow: "The novel",
    title: "Album based off the book.",
    lead: "Exploit — Book One of The Universe: a supernatural thriller that plays like a movie — hidden power, an unseen war, and a light that heals what it touches. Every song on this record is sung from inside the story.",
    body: [ { n: "p", i: { n: "a", href: "/exploit", c: "egt-cta",
      i: [ "Learn more", { n: "span", c: "egt-cta__tick", i: " ›" } ] } } ]
  }) : null;

  return S.page(reserve, [

    albumSchema(a, streamable),

    /* HERO — frosted card floating on the shared cinematic still. UNDIMMED media
     * (contrast is the glass, never a scrim, §4). The cover is a foreground image
     * inside the glass, not a stretched full-bleed background. */
    { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-album-hero",
      i: [
        S.media({ img: "/imgs/st-listen.jpg", alt: "", parallax: 0.06, priority: true }),
        { n: "div", c: "egt__shell egt-canvas__inner", i: heroCard }
      ] },

    /* TRACKLIST — no "Tracklist" eyebrow/heading; the hero already names the
     * record and its released/"Releases on <date>" posture, so the list stands
     * on its own. Rows come from the SHARED builder (egt.trackrow.js), then a
     * repeat of the listen/pre-save affordance at the foot. */
    { n: "section", c: "egt-section egt-album-list",
      i: { n: "div", c: "egt__shell",
        i: [
          { n: "ol", c: "egt-album-trks", role: "list", "aria-label": a.nm + " tracklist",
            i: tracks.map(function (t, i) { return TR.trackRow(a.id, t, i, a.explicit); }) }
        ].concat(bottomPosture
          ? [ { n: "div", c: "egt-album-list__foot egt-reveal", i: bottomPosture } ]
          : []) } },

    /* FREE SAMPLE — the single email capture on this page, on every album
     * (built above). egt.capture.js ships with the shared footer, so the form
     * self-wires with nothing added to the page JSON. */
    sampleCta,

    /* BOOK — Exploit records funnel to the free opening (null → dropped below) */
    bookCta,

    /* BACK to Entertainment */
    { n: "section", c: "egt-section egt-album-back",
      i: { n: "div", c: "egt__shell egt-reveal",
        i: { n: "a", href: "/entertainment", c: "egt-album-back__link",
          i: [ { n: "span", c: "egt-album-back__arrow", "aria-hidden": "true", i: "‹ " },
            "Back to Entertainment" ] } } }
  ].filter(Boolean), { footerCaptureLabel: S.FOOTER_LABELS.entertainment });
};
